// Stylovací funkce pro OpenLayers features: 4 vizualizační módy (elevation, typ barva, typ tvar, obs_type) + exportovaná legenda
import { Style, Fill, Stroke, Circle, Text } from 'ol/style'

// ── Popisek stanice na mapě ─────────────────────────────────────────────────
// labelFields: pole kombinovatelných polí, každé z 'id' | 'name' | 'qual' | 'full_name' | 'wsi'
// (multi-select checkboxy v toolbaru, viz LABEL_MODES v StationMap.jsx) — vybrané hodnoty
// stanice se spojí čárkou, prázdné/chybějící hodnoty se přeskočí. Prázdné pole → žádný popisek.
const LABEL_FIELD_GETTERS = {
  id:        s => s.gh_id,
  name:      s => s.name,
  qual:      s => s.qual,
  full_name: s => s.full_name,
  wsi:       s => s.wsi,
}

export function stationLabel(labelFields, station) {
  if (!station || !labelFields || labelFields.length === 0) return null
  const parts = labelFields.map(f => LABEL_FIELD_GETTERS[f]?.(station)).filter(Boolean)
  return parts.length > 0 ? parts.join(', ') : null
}

// ── Elevation colour scale ──────────────────────────────────────────────────
export function elevationColor(elevation) {
  if (elevation == null) return '#aaa'
  if (elevation < 150)  return '#f5f5dc'  // cream
  if (elevation < 250)  return '#e8e07a'  // light yellow
  if (elevation < 300)  return '#d4c84a'  // yellow
  if (elevation < 500)  return '#e0a020'  // yellow-orange
  if (elevation < 750)  return '#d07010'  // orange
  if (elevation < 1000) return '#b84000'  // dark orange
  if (elevation < 1250) return '#8b2000'  // red-brown
  if (elevation < 1500) return '#5a1500'  // dark brown
  return '#2a0a00'                        // very dark brown
}

// ── Station type colours ────────────────────────────────────────────────────
// Pořadí je záměrné: první barvy jsou "klidné" (modrá/zelená/teal), teprve pak přijdou
// křiklavé odstíny (žlutá, sytě oranžová, purpurová). Ve variantě 'freq' (viz buildTypeColors)
// se typy řadí od nejpočetnějšího a berou barvy v tomto pořadí, takže nejvíc zastoupené typy
// dostanou klidnější barvu a křiklavé barvy zůstanou vyhrazené pro vzácné typy.
export const TYPE_PALETTE = [
  '#2196f3', // blue
  '#4caf50', // green
  '#00bcd4', // cyan
  '#795548', // brown
  '#3f51b5', // indigo
  '#8bc34a', // light green
  '#607d8b', // blue grey
  '#009688', // teal
  '#9c27b0', // purple
  '#ff9800', // orange
  '#00bfa5', // teal accent
  '#673ab7', // deep purple
  '#03a9f4', // light blue
  '#1b5e20', // dark green
  '#e91e63', // pink
  '#4a148c', // deep purple dark
  '#ff5722', // deep orange
  '#bf360c', // deep orange dark
  '#2962ff', // blue accent
  '#aa00ff', // purple accent
  '#d50000', // red accent
  '#f44336', // red
  '#ffc107', // amber
  '#cddc39', // lime
  '#ff6d00', // orange accent
]

// Stabilní index z řetězce id typu stanice (0..mod-1) — nezávislý na tom, kolik dalších typů
// je zrovna v poli (API /station_type vrací po přihlášení jiný/širší výběr typů, takže index
// v poli by se posouval a barvy by se po loginu přeskládaly). Používá se pro varianty
// 'alpha'/'hsl', kde barva má být čistě funkcí id typu stanice, ne jeho pozice mezi aktuálně
// načtenými typy.
function stableIndex(id, mod) {
  let hash = 0
  for (let i = 0; i < id.length; i++) {
    hash = (hash * 31 + id.charCodeAt(i)) | 0
  }
  return Math.abs(hash) % mod
}

// Barevné stopy teplotní škály (nejteplejší → nejchladnější), mezi kterými se lineárně
// interpoluje podle abecední pozice id — viz alphaTemperatureColor.
const TEMPERATURE_STOPS = [
  [120, 0, 0],   // tmavě červená (A...)
  [200, 30, 0],  // červená
  [230, 90, 0],  // oranžová
  [210, 170, 0], // žlutooranžová
  [40, 140, 30], // zelená
  [0, 110, 130], // modrozelená
  [20, 60, 170], // modrá
  [70, 20, 130], // fialová (Z...)
]

function lerp(a, b, t) {
  return a + (b - a) * t
}

// Vrátí barvu z lineární teplotní škály pro pozici `i` mezi `count` rovnoměrně rozprostřenými
// typy (i = 0 → nejteplejší, i = count-1 → nejchladnější). Škála se generuje přesně na počet
// aktuálně načtených typů, takže se vždy využije celý rozsah barev bez ohledu na to, jak jsou
// abecedně rozmístěné (mezera v abecedě mezi sousedními typy by jinak barvy nerovnoměrně
// "spotřebovala").
function temperatureColorAt(i, count) {
  const frac = count > 1 ? i / (count - 1) : 0
  const segments = TEMPERATURE_STOPS.length - 1
  const pos = frac * segments
  const idx = Math.min(segments - 1, Math.max(0, Math.floor(pos)))
  const t = pos - idx
  const [r1, g1, b1] = TEMPERATURE_STOPS[idx]
  const [r2, g2, b2] = TEMPERATURE_STOPS[idx + 1]
  const r = Math.round(lerp(r1, r2, t))
  const g = Math.round(lerp(g1, g2, t))
  const b = Math.round(lerp(b1, b2, t))
  return `rgb(${r}, ${g}, ${b})`
}

// Přiřadí barvám seznam typů stanic. `variant` volí strategii:
//  - 'db': barva natvrdo přiřazená typu v tabulce station_type (sloupec color, viz cw-server
//    migrace add-color-to-station-type) — `stationTypes` zde musí být pole objektů
//    { id, color }, ne jen pole id. Příbuzné typy (AKS*/AMS*) mají v DB podobný, ale
//    odlišitelný odstín.
//  - 'alpha' (výchozí): barva daná stabilním hashem id (viz stableIndex) — stejné id má
//    vždy stejnou barvu bez ohledu na to, jaké další typy API zrovna vrátilo (liší se podle
//    přihlášení uživatele).
//  - 'freq': vyžaduje `counts` (počet stanic na typ) — typy seřazené od nejpočetnějšího
//    berou barvy z TYPE_PALETTE popořadě, takže nejčastější typ dostane klidnou barvu
//    z počátku palety a vzácné typy naopak sytější barvy z konce. Pořadí je závislé na
//    aktuálně načtených datech, proto tato varianta stabilitu napříč přihlášením negarantuje.
//  - 'hsl': lineární "teplotní" škála podle abecedy, natažená přesně na počet aktuálně
//    načtených typů — abecedně první typ dostane tmavě červenou (nejteplejší), poslední
//    fialovou/modrou (nejchladnější), plynule přes oranžovou → žlutou → zelenou → modrou.
//    Barvy se přiřazují podle pořadí v seřazeném seznamu, ne podle abecední hodnoty id, takže
//    škála je vždy rovnoměrně využitá celá, i když jsou mezi typy v abecedě velké mezery
//    (jinak by se škála "spotřebovala" nerovnoměrně). Konkrétní barvy se proto mohou lišit
//    podle přihlášení, protože API /station_type vrací po přihlášení jiný výběr typů — u této
//    varianty to záměrně nevadí.
export function buildTypeColors(stationTypes, counts = null, variant = 'alpha') {
  const colors = {}

  if (variant === 'db') {
    stationTypes.forEach((t) => {
      colors[t.id] = t.color || '#607d8b'
    })
    return colors
  }

  if (variant === 'freq' && counts) {
    const sortedTypes = [...stationTypes].sort((a, b) => (counts[b] || 0) - (counts[a] || 0) || (a < b ? -1 : 1))
    sortedTypes.forEach((type, i) => {
      colors[type] = TYPE_PALETTE[i % TYPE_PALETTE.length]
    })
    return colors
  }

  if (variant === 'hsl') {
    const sortedTypes = [...stationTypes].sort()
    sortedTypes.forEach((type, i) => {
      colors[type] = temperatureColorAt(i, sortedTypes.length)
    })
    return colors
  }

  stationTypes.forEach((type) => {
    colors[type] = TYPE_PALETTE[stableIndex(type, TYPE_PALETTE.length)]
  })
  return colors
}

function typeColor(stationType, typeColors) {
  return (typeColors && typeColors[stationType]) || '#607d8b'
}

// ── Element colours (prvky) per obs_type ────────────────────────────────────
// Barvy sjednocené podle "druhu veličiny" (teplota=červená, srážky=modrá, svit=žlutá,
// sníh=světle modrá, půdní teplota=hnědá, tlak=fialová, vítr=zelená, radiace=oranžová),
// aby vizualizace HLY/10M vypadala konzistentně s DLY, i když se zkratky liší.
export const EL_COLORS = {
  // DLY
  T:       '#f44336', // Teploty
  SRA:     '#2196f3', // Srážky
  SSV:     '#ffc107', // Sluneční svit
  SCE:     '#90caf9', // Sníh
  T10:     '#795548', // Půdní teploty
  P:       '#ae52be', // Tlak
  F:       '#4caf50', // Vítr
  RGLB_D:  '#ff8800', // Solární radiace
  // HLY
  Td:      '#f44336', // Rosný bod (teplota)
  SRA1H:   '#2196f3', // Srážky
  SSV1H:   '#ffc107', // Sluneční svit
  P_hm:    '#ae52be', // Tlak
  RGLB1H:  '#ff8800', // Solární radiace
  // 10M
  SRA10M:  '#2196f3', // Srážky
  SSV10M:  '#ffc107', // Sluneční svit
  SCEa:    '#90caf9', // Sníh
  RGLB10:  '#ff8800', // Solární radiace
}

export const EL_PRIORITY = ['T', 'SRA', 'SSV', 'SCE', 'T10', 'P', 'F', 'RGLB_D']
export const HLY_EL_PRIORITY = ['Td', 'SRA1H', 'SSV1H', 'T10', 'P_hm', 'F', 'RGLB1H']
export const MIN10_EL_PRIORITY = ['T', 'SRA10M', 'SSV10M', 'SCEa', 'T10', 'P', 'F', 'RGLB10']

function getElementsByObsType(observation, obsType, priority) {
  if (!observation || observation.length === 0) return []
  const els = new Set(
    observation.filter(o => o.obs_type === obsType).map(o => o.el_abbreviation)
  )
  return priority.filter(el => els.has(el))
}

export function getDlyElements(observation) {
  return getElementsByObsType(observation, 'DLY', EL_PRIORITY)
}

export function getHlyElements(observation) {
  return getElementsByObsType(observation, 'HLY', HLY_EL_PRIORITY)
}

export function get10mElements(observation) {
  return getElementsByObsType(observation, '10M', MIN10_EL_PRIORITY)
}

// ── obs_type colours (type-shape mode) ──────────────────────────────────────
export const OBS_TYPE_COLORS = {
  DLY:   '#2196f3', // denní
  HLY:   '#4caf50', // hodinové
  '10M': '#ff9800', // desetiminutové
  PHENO: '#9c27b0', // fenologie
}
export const OBS_TYPE_PRIORITY = ['DLY', 'HLY', '10M', 'PHENO']

export function getObsTypes(observation) {
  if (!observation || observation.length === 0) return []
  const types = new Set(observation.map(o => o.obs_type))
  return OBS_TYPE_PRIORITY.filter(t => types.has(t))
}

export function buildTypeLegend(typeColors) {
  return Object.entries(typeColors)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([k, v]) => ({ color: v, label: k }))
    .concat([{ color: '#607d8b', label: 'ostatní' }])
}

// ── Build style ────────────────────────────────────────────────────────────
export function makeFeatureStyle(vizMode, labelMode, feature, isSelected, typeColors = {}) {
  const station = feature.get('station')
  if (!station) return null

  let fillColor = '#2196f3'

  switch (vizMode) {
    case 'elevation':
      fillColor = elevationColor(station.elevation)
      break
    case 'type-color':
    case 'type-color-alpha':
    case 'type-color-freq':
    case 'type-color-hsl':
      fillColor = typeColor(station.station_type, typeColors)
      break
    default:
      break
  }

  const strokeColor = isSelected ? '#fff' : 'rgba(255,255,255,0.8)'
  const strokeWidth = isSelected ? 3 : 1.5
  const radius = isSelected ? 9 : 6

  const image = new Circle({
    radius,
    fill: new Fill({ color: fillColor }),
    stroke: new Stroke({ color: strokeColor, width: strokeWidth }),
  })

  const labelText = stationLabel(labelMode, station)
  const text = labelText ? new Text({
    text: labelText,
    offsetY: -12,
    font: '10px sans-serif',
    fill: new Fill({ color: '#333' }),
    stroke: new Stroke({ color: '#fff', width: 2 }),
  }) : null

  return new Style({ zIndex: isSelected ? 9999 : 0, image, text })
}

// Legend entries for each visualization mode
export const LEGEND = {
  elevation: [
    { color: '#f5f5dc', label: '< 150 m n.m.' },
    { color: '#e8e07a', label: '150–250 m n.m.' },
    { color: '#d4c84a', label: '250–300 m n.m.' },
    { color: '#e0a020', label: '300–500 m n.m.' },
    { color: '#d07010', label: '500–750 m n.m.' },
    { color: '#b84000', label: '750–1000 m n.m.' },
    { color: '#8b2000', label: '1000–1250 m n.m.' },
    { color: '#5a1500', label: '1250–1500 m n.m.' },
    { color: '#2a0a00', label: '> 1500 m n.m.' },
  ],
  'type-color': [], // dynamicky plněno v StationMap přes buildTypeLegend(typeColors)
  'type-shape': [
    { color: OBS_TYPE_COLORS.DLY,    label: 'DLY – denní' },
    { color: OBS_TYPE_COLORS.HLY,    label: 'HLY – hodinové' },
    { color: OBS_TYPE_COLORS['10M'], label: '10M – desetiminutové' },
    { color: OBS_TYPE_COLORS.PHENO,  label: 'PHENO – fenologie' },
    { color: '#aaa',                 label: 'bez dat' },
  ],
  'obs-color': [
    { color: EL_COLORS.T,      label: 'T – Teploty' },
    { color: EL_COLORS.SRA,    label: 'SRA – Srážky' },
    { color: EL_COLORS.SSV,    label: 'SSV – Sluneční svit' },
    { color: EL_COLORS.SCE,    label: 'SCE – Sníh' },
    { color: EL_COLORS.T10,    label: 'T10 – Půdní teploty' },
    { color: EL_COLORS.P,      label: 'P – Tlak' },
    { color: EL_COLORS.F,      label: 'F – Vítr' },
    { color: EL_COLORS.RGLB_D, label: 'RGLB_D – Solární radiace' },
    { color: '#aaa',           label: 'bez DLY dat' },
  ],
  'obs-color-hly': [
    { color: EL_COLORS.Td,     label: 'Td – Rosný bod' },
    { color: EL_COLORS.SRA1H,  label: 'SRA1H – Srážky' },
    { color: EL_COLORS.SSV1H,  label: 'SSV1H – Sluneční svit' },
    { color: EL_COLORS.T10,    label: 'T10 – Půdní teploty' },
    { color: EL_COLORS.P_hm,   label: 'P_hm – Tlak' },
    { color: EL_COLORS.F,      label: 'F – Vítr' },
    { color: EL_COLORS.RGLB1H, label: 'RGLB1H – Solární radiace' },
    { color: '#aaa',           label: 'bez HLY dat' },
  ],
  'obs-color-10m': [
    { color: EL_COLORS.T,      label: 'T – Teploty' },
    { color: EL_COLORS.SRA10M, label: 'SRA10M – Srážky' },
    { color: EL_COLORS.SSV10M, label: 'SSV10M – Sluneční svit' },
    { color: EL_COLORS.SCEa,   label: 'SCEa – Sníh' },
    { color: EL_COLORS.T10,    label: 'T10 – Půdní teploty' },
    { color: EL_COLORS.P,      label: 'P – Tlak' },
    { color: EL_COLORS.F,      label: 'F – Vítr' },
    { color: EL_COLORS.RGLB10, label: 'RGLB10 – Solární radiace' },
    { color: '#aaa',           label: 'bez 10M dat' },
  ],
}
